import React from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Button, ButtonGroup, Flex, Menu, MenuDivider, MenuItem, Popover, Text, Tooltip } from 'core/components';
import { FormState, RemoveMenuItem } from 'core/form';
import { Classes, PopoverPosition } from '@blueprintjs/core';
import styled from 'styled-components';
import { BOARD_LABEL } from '@kentik/ui-shared/boards/boards';
import BoardModel from '../../stores/boards/BoardModel';
import BoardPanelModel from '../../stores/boards/BoardPanelModel';
import { AddPanelButton } from './AddPanelButton';

const Wrapper = styled.div`
  // force buttons to be shorter
  .${Classes.BUTTON} {
    min-height: 26px;
  }
`;

interface Props {
  board: BoardModel;
  panel?: BoardPanelModel;
  form?: FormState;
  showAddButton?: boolean;
  onSave: () => Promise<void> | void;
  onAddPanel: (type: string) => Promise<void> | void;
  onCancel: () => void;
  onShowSettings: () => void;
}

const BoardSubnavTools = ({
  board,
  panel,
  form,
  showAddButton = true,
  onSave,
  onAddPanel,
  onCancel,
  onShowSettings
}: Props) => {
  const history = useHistory();
  const { canEdit } = board;
  const isPanelView = !!panel;
  const isFormValid = form ? form.valid : true;
  const hasUnsavedChanges = panel ? panel.isDirty || form?.dirty : board.isDirty;
  const isReady = panel ? panel.isReady : true;
  const isSaveEnabled = isReady && isFormValid && hasUnsavedChanges;
  const isAddEnabled = isReady && isFormValid;
  // For UDE panels, the Save / Add-to-dashboard actions are hidden (not just disabled)
  // until the user has executed a query.
  const isUdePanel = panel?.get('type') === 'ude';
  const showSaveActions = !isUdePanel || !!panel?.queryModel?.hasRun;
  const isSaving =
    (!board.get('draft') &&
      !board.isSilentSaving &&
      (board.isRequestActive('creating') || board.isRequestActive('updating'))) ||
    (isPanelView && !panel.get('draft') && (panel.isRequestActive('creating') || panel.isRequestActive('updating')));

  const basePath = history.location.pathname.startsWith('/v4/ude') ? '/v4/ude' : '/v4/library';

  const handleSave = async () => {
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }
    await onSave();
  };

  const handleAddPanel = async (type: string) => {
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }
    await onAddPanel(type);
  };

  const handleAddToDashboard = async () => {
    if (panel && form) {
      const values = form.getValues();
      panel.safeSet(values);
    }

    const boardConfig = board.get('config');
    await board.save({ config: { ...boardConfig, isDashboard: true } });
    history.push(`${basePath}/boards/${board.id}`);
  };

  const handleBackToDashboard = () => {
    history.push(`${basePath}/boards/${board.id}`);
  };

  const handleRevertBoard = async () => {
    await board.discardDraftPanels();
  };

  const handleDuplicate = async () => {
    const duplicateBoard = board.duplicate({ save: false });
    await duplicateBoard.save();
    history.push(duplicateBoard.navigatePath, { isEditing: true });
  };

  const handleRemove = async () => {
    await board.destroy();
    history.push('/v4/library');
  };

  const showActionsMenu = canEdit && !isPanelView;
  const showAddPanel = !isPanelView && showAddButton && canEdit;
  const showRevert = canEdit && !board.isNew && !isPanelView && board.isDirty;

  const actionsMenu = (
    <Menu>
      <MenuItem icon="cog" text="Settings" onClick={onShowSettings} />
      <MenuItem icon="duplicate" text="Duplicate" onClick={handleDuplicate} />
      {board.canRemove && (
        <>
          <MenuDivider />
          <RemoveMenuItem
            model={board}
            entityName={board.typeLabel}
            text="Delete"
            onRemove={handleRemove}
            confirmButtonText="Delete"
          />
        </>
      )}
    </Menu>
  );

  const saveMenu = (
    <Menu>
      <MenuItem
        icon="undo"
        text="Revert"
        disabled={!board.isDirty || board.isRequestActive('destroying')}
        onClick={handleRevertBoard}
      />
    </Menu>
  );

  return (
    <Wrapper>
      <Flex alignItems="center" gap={1}>
        {showActionsMenu && (
          <Popover
            content={actionsMenu}
            position={PopoverPosition.BOTTOM_RIGHT}
            modifiers={{ offset: { enabled: true, offset: '0,2px' } }}
          >
            <Button rightIcon="caret-down" minimal text="Actions" data-testid="BoardActionsButton" />
          </Popover>
        )}

        {showAddPanel && (
          <AddPanelButton
            board={board}
            icon="plus"
            minimal
            text="Add Panel"
            disabled={!isAddEnabled}
            onAddPanel={handleAddPanel}
          />
        )}
        {isPanelView && board.singlePanelView && canEdit && showSaveActions && (
          <Button text={`Add to ${BOARD_LABEL}`} minimal disabled={!isAddEnabled} onClick={handleAddToDashboard} />
        )}
        {isPanelView && !board.singlePanelView && (
          <Button text={`Back to ${BOARD_LABEL}`} minimal onClick={handleBackToDashboard} />
        )}
        {canEdit &&
          showSaveActions &&
          (isPanelView ? (
            <ButtonGroup>
              <Tooltip
                content="You have unsaved changes"
                disabled={!hasUnsavedChanges}
                position={PopoverPosition.BOTTOM}
              >
                <Button
                  text="Save"
                  icon={hasUnsavedChanges ? 'dot' : undefined}
                  iconColor="primary"
                  disabled={!isSaveEnabled}
                  loading={isSaving}
                  onClick={handleSave}
                />
              </Tooltip>
              {!panel.isNew && !(board.singlePanelView && panel.hasOnlyDraftVersion) && (
                <Popover
                  content={
                    <Menu>
                      <MenuItem icon="undo" text="Discard Changes" disabled={!panel.isDirty} onClick={onCancel} />
                    </Menu>
                  }
                  position={PopoverPosition.BOTTOM_RIGHT}
                  modifiers={{ offset: { enabled: true, offset: '0,2px' } }}
                  disabled={!panel.isDirty}
                >
                  <Button rightIcon="caret-down" disabled={!panel.isDirty} />
                </Popover>
              )}
            </ButtonGroup>
          ) : (
            <ButtonGroup>
              <Tooltip
                content="You have unsaved changes"
                disabled={!hasUnsavedChanges}
                position={PopoverPosition.BOTTOM}
              >
                <Button
                  text={isSaving ? <Text muted>Saving...</Text> : 'Save'}
                  icon={hasUnsavedChanges && !isSaving ? 'dot' : null}
                  fontWeight={500}
                  iconColor="primary"
                  disabled={!isSaveEnabled}
                  onClick={handleSave}
                  minWidth={80}
                />
              </Tooltip>
              <Popover
                content={saveMenu}
                position={PopoverPosition.BOTTOM_RIGHT}
                modifiers={{ offset: { enabled: true, offset: '0,2px' } }}
                disabled={!showRevert}
              >
                <Button rightIcon="caret-down" disabled={!showRevert} data-testid="BoardSaveMenuButton" />
              </Popover>
            </ButtonGroup>
          ))}
      </Flex>
    </Wrapper>
  );
};

export default observer(BoardSubnavTools);
